// ElevenLabsService.js
export class ElevenLabsService {
  constructor(apiKey) {
    this.apiKey = apiKey;
    this.baseUrl = "https://api.elevenlabs.io/v1";
  }

  async getVoices() {
    try {
      const response = await fetch(`${this.baseUrl}/voices`, {
        headers: {
          'xi-api-key': this.apiKey
        }
      });
      
      if (!response.ok) throw new Error('Failed to fetch voices');
      return await response.json();
    } catch (error) {
      console.error('Error fetching voices:', error);
      throw error;
    }
  }

  async streamText(text, voiceId, options = {}) {
    const defaults = {
      model_id: "eleven_multilingual_v2",
      voice_settings: {
        stability: 0.5,
        similarity_boost: 0.75,
        style: 0.7,
        speaking_rate: 1,
      },
      ...options
    };

    try {
      const response = await fetch(
        `${this.baseUrl}/text-to-speech/${voiceId}/stream`,
        {
          method: 'POST',
          headers: {
            'xi-api-key': this.apiKey,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            text,
            ...defaults,
            stream: true
          })
        }
      );

      if (!response.ok) throw new Error('Failed to generate speech');
      
      return response.body;
    } catch (error) {
      console.error('Error streaming audio:', error);
      throw error;
    }
  }

  async playAudioStream(stream, onStart, onEnd, onError) {
    try {
      const mediaSource = new MediaSource();
      const audio = new Audio();
      audio.src = URL.createObjectURL(mediaSource);

      return new Promise((resolve, reject) => {
        mediaSource.addEventListener('sourceopen', async () => {
          try {
            const sourceBuffer = mediaSource.addSourceBuffer('audio/mpeg');
            const reader = stream.getReader();
            const chunks = [];
            let isFirstChunk = true;
            
            const appendNextChunk = () => {
              if (chunks.length && !sourceBuffer.updating) {
                const chunk = chunks.shift();
                sourceBuffer.appendBuffer(chunk);
              }
            };

            sourceBuffer.addEventListener('updateend', () => {
              if (chunks.length) {
                appendNextChunk();
              } else if (mediaSource.readyState === 'open' && !sourceBuffer.updating) {
                try {
                  mediaSource.endOfStream();
                } catch (error) {
                  console.warn('EndOfStream warning:', error);
                }
              }
            });

            const processStream = async () => {
              try {
                while (true) {
                  const { done, value } = await reader.read();
                  
                  if (done) {
                    if (!sourceBuffer.updating && mediaSource.readyState === 'open') {
                      try {
                        mediaSource.endOfStream();
                      } catch (error) {
                        console.warn('EndOfStream warning:', error);
                      }
                    }
                    break;
                  }

                  chunks.push(value);
                  
                  if (isFirstChunk) {
                    isFirstChunk = false;
                    appendNextChunk();
                  } else if (!sourceBuffer.updating) {
                    appendNextChunk();
                  }
                }
              } catch (error) {
                onError?.(error);
                reject(error);
              }
            };

            audio.onplay = () => onStart?.();
            audio.onended = () => {
              onEnd?.();
              resolve();
            };
            audio.onerror = (error) => {
              onError?.(error);
              reject(error);
            };

            await processStream();
            await audio.play();
          } catch (error) {
            onError?.(error);
            reject(error);
          }
        });
      });
    } catch (error) {
      onError?.(error);
      throw error;
    }
  }
}